import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import './ShaderGlowButton.css';

interface ShaderGlowButtonProps {
    isActive: boolean;
    children: React.ReactNode;
    className?: string;
    onClick?: () => void;
    title?: string;
    glowColor?: string;
    intensity?: number;
}

// WebGL Error Boundary - catches Canvas initialization failures
class WebGLErrorBoundary extends React.Component<
    { children: React.ReactNode; fallback: React.ReactNode },
    { hasError: boolean }
> {
    constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): { hasError: boolean } {
        // Catch WebGL-related errors
        const isWebGLError =
            error.message?.includes('WebGL') ||
            error.message?.includes('context') ||
            error.message?.includes('canvas') ||
            error.message?.includes('THREE') ||
            error.name === 'WebGLContextLost';

        if (isWebGLError) {
            console.warn('WebGL unavailable, falling back to CSS effects:', error.message);
            return { hasError: true };
        }

        // Re-throw non-WebGL errors
        throw error;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        console.warn('WebGL Error Boundary caught:', error, errorInfo);
    }

    render(): React.ReactNode {
        if (this.state.hasError) {
            return this.props.fallback;
        }
        return this.props.children;
    }
}

// Custom Fragment Shader - simple, consistent glow that follows icon shape
const glowFragmentShader = `
uniform float time;
uniform vec3 glowColor;
uniform float intensity;
uniform bool isActive;
uniform bool isHovered;
uniform sampler2D maskTexture;
uniform bool useMask;
varying vec2 vUv;

// Sample mask with clamped UV to prevent edge-wrap artifacts
float sampleMaskAlpha(vec2 uv) {
    vec2 clampedUv = clamp(uv, 0.001, 0.999);
    return texture2D(maskTexture, clampedUv).a;
}

// Compute signed distance field from icon shape
float computeSDF(vec2 uv) {
    if (!useMask) {
        vec2 center = vec2(0.5, 0.5);
        return distance(uv, center);
    }
    
    float maskAlpha = sampleMaskAlpha(uv);
    
    float sdf = 0.0;
    float searchRadius = 0.12;
    int samples = 8;
    
    for (int i = 0; i < samples; i++) {
        float angle = float(i) * 6.28318 / float(samples);
        vec2 offset = vec2(cos(angle), sin(angle)) * searchRadius;
        sdf += (1.0 - sampleMaskAlpha(uv + offset));
    }
    
    sdf /= float(samples);
    return maskAlpha > 0.5 ? -maskAlpha : sdf;
}

void main() {
    vec2 uv = vUv;
    float sdf = computeSDF(uv);
    float dist = abs(sdf);
    
    // Softer falloffs to reduce aliasing artifacts at low resolution
    float iconGlow = smoothstep(0.35, 0.02, dist);
    float haloGlow = smoothstep(0.5, 0.05, dist);
    
    // Gentle pulse (same for all icons) - subtle 0.95 to 1.05 range
    float pulse = sin(time * 1.5) * 0.025 + 1.0;
    
    float glowIntensity;
    if (isActive) {
        glowIntensity = (iconGlow * 1.2 + haloGlow * 0.4) * intensity * pulse;
    } else {
        glowIntensity = (iconGlow * 0.35 + haloGlow * 0.15) * intensity;
    }
    
    if (isHovered) {
        glowIntensity *= 1.15;
    }
    
    glowIntensity = max(0.0, glowIntensity);
    
    // Threshold to eliminate low-intensity speckles/artifacts
    glowIntensity = glowIntensity < 0.04 ? 0.0 : glowIntensity;
    
    vec3 finalColor = glowColor * glowIntensity;
    float alpha = glowIntensity;
    gl_FragColor = vec4(finalColor, alpha);
}
`;

const glowVertexShader = `
varying vec2 vUv;

void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// Shader plane component that renders the glow effect
const GlowPlane: React.FC<{
    isActive: boolean;
    isHovered: boolean;
    glowColor: string;
    intensity: number;
    maskTexture?: THREE.Texture | null;
}> = ({ isActive, isHovered, glowColor, intensity, maskTexture }) => {
    const meshRef = useRef<THREE.Mesh>(null);

    // Parse hex color to RGB
    const color = useMemo(() => {
        const hex = glowColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16) / 255;
        const g = parseInt(hex.substr(2, 2), 16) / 255;
        const b = parseInt(hex.substr(4, 2), 16) / 255;
        return new THREE.Vector3(r, g, b);
    }, [glowColor]);

    // Create a default transparent texture
    const defaultTexture = useMemo(() => {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = 'rgba(255, 255, 255, 0)';
            ctx.fillRect(0, 0, 1, 1);
        }
        const tex = new THREE.CanvasTexture(canvas);
        tex.wrapS = THREE.ClampToEdgeWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        return tex;
    }, []);

    // Custom shader material - only recreate when color or intensity changes
    // isActive and isHovered are updated via uniforms in useFrame to avoid recreation
    const shaderMaterial = useMemo(
        () =>
            new THREE.ShaderMaterial({
                uniforms: {
                    time: { value: 0 },
                    glowColor: { value: color },
                    intensity: { value: intensity },
                    isActive: { value: false }, // Initial value, updated in useFrame
                    isHovered: { value: false }, // Initial value, updated in useFrame
                    maskTexture: { value: maskTexture || defaultTexture },
                    useMask: { value: !!maskTexture },
                },
                vertexShader: glowVertexShader,
                fragmentShader: glowFragmentShader,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
            }),
        [color, intensity, maskTexture, defaultTexture] // Recreate when mask changes
    );

    // Cleanup: dispose material when component unmounts
    useEffect(() => {
        return () => {
            shaderMaterial.dispose();
            defaultTexture.dispose();
        };
    }, [shaderMaterial, defaultTexture]);

    // Update mask texture when it changes
    useEffect(() => {
        if (maskTexture) {
            shaderMaterial.uniforms.maskTexture.value = maskTexture;
            shaderMaterial.uniforms.useMask.value = true;
        }
    }, [maskTexture, shaderMaterial]);

    // Animate the shader and update dynamic uniforms
    useFrame((state) => {
        if (meshRef.current) {
            shaderMaterial.uniforms.time.value = state.clock.getElapsedTime();
            shaderMaterial.uniforms.isActive.value = isActive;
            shaderMaterial.uniforms.isHovered.value = isHovered;
        }
    });

    return (
        <mesh ref={meshRef} material={shaderMaterial}>
            <planeGeometry args={[2, 2]} />
        </mesh>
    );
};

const ShaderGlowButton: React.FC<ShaderGlowButtonProps> = ({
    isActive,
    children,
    className = '',
    onClick,
    title,
    glowColor = '#6B6FFF',
    intensity = 1.0,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [webglSupported, setWebglSupported] = useState<boolean | null>(null);
    const [maskTexture, setMaskTexture] = useState<THREE.Texture | null>(null);
    const maskTextureRef = useRef<THREE.Texture | null>(null);
    maskTextureRef.current = maskTexture;
    const contentRef = useRef<HTMLDivElement>(null);

    // Check WebGL support on mount
    useEffect(() => {
        const checkWebGL = (): boolean => {
            try {
                const canvas = document.createElement('canvas');
                const gl =
                    canvas.getContext('webgl') ||
                    canvas.getContext('experimental-webgl');
                return !!gl;
            } catch (e) {
                return false;
            }
        };

        setWebglSupported(checkWebGL());
    }, []);

    // Create texture from icon content for shape masking
    const updateMaskTexture = useCallback(() => {
        if (!contentRef.current || !webglSupported) return;

        try {
            const element = contentRef.current;
            const rect = element.getBoundingClientRect();
            
            // Create an off-screen canvas to capture the icon
            const canvas = document.createElement('canvas');
            const size = 256; // Texture resolution
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            
            if (!ctx) return;

            // Clear canvas
            ctx.clearRect(0, 0, size, size);
            
            // Use a uniform circular mask for ALL icons - ensures consistent glow
            // across OpenAI, Anthropic, Gemini, Grok, etc. and eliminates artifacts
            // from complex image alpha (e.g. anthropic PNG's irregular transparency)
            const padding = size * 0.15;
            const radius = (size - padding * 2) / 2;
            ctx.fillStyle = 'white';
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, radius, 0, Math.PI * 2);
            ctx.fill();
            
            // Create Three.js texture from canvas
            const texture = new THREE.CanvasTexture(canvas);
            texture.wrapS = THREE.ClampToEdgeWrapping;
            texture.wrapT = THREE.ClampToEdgeWrapping;
            texture.needsUpdate = true;
            
            // Clean up old texture (use ref to avoid dependency loop)
            if (maskTextureRef.current) {
                maskTextureRef.current.dispose();
            }
            
            setMaskTexture(texture);
        } catch (error) {
            console.warn('Failed to create mask texture:', error);
        }
    }, [webglSupported]);

    // Update mask texture when content changes or loads
    useEffect(() => {
        if (!webglSupported) return;

        // Initial update
        const timer = setTimeout(updateMaskTexture, 100);

        // Update when images load
        const element = contentRef.current;
        const img = element?.querySelector('img');
        
        const handleImageLoad = () => {
            updateMaskTexture();
        };

        if (img) {
            img.addEventListener('load', handleImageLoad);
            if (img.complete) {
                updateMaskTexture();
            }
        } else {
            // For SVG icons, update immediately
            updateMaskTexture();
        }

        return () => {
            clearTimeout(timer);
            if (img) {
                img.removeEventListener('load', handleImageLoad);
            }
        };
    }, [webglSupported, children, updateMaskTexture]);

    // CSS fallback component for when WebGL is unavailable
    const CSSFallback = (
        <div
            className={`shader-glow-button shader-glow-button-css-fallback ${className} ${isActive ? 'active' : ''
                } ${isHovered ? 'hovered' : ''}`}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            title={title}
            style={{
                // Apply CSS-based glow as fallback with shape following
                filter: isActive
                    ? `drop-shadow(0 0 4px ${glowColor}) drop-shadow(0 0 8px ${glowColor})`
                    : `drop-shadow(0 0 2px ${glowColor})`,
                transition: 'filter 0.3s ease',
            }}
        >
            <div className="shader-glow-content" ref={contentRef}>{children}</div>
        </div>
    );

    // Don't render until WebGL check is complete
    if (webglSupported === null) {
        return CSSFallback;
    }

    // Use CSS fallback if WebGL is not supported
    if (webglSupported === false) {
        return CSSFallback;
    }

    // Render WebGL version with error boundary
    return (
        <WebGLErrorBoundary fallback={CSSFallback}>
            <div
                className={`shader-glow-button ${className}`}
                onClick={onClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                title={title}
            >
                {/* WebGL Canvas for shader glow effect */}
                <div className="shader-glow-canvas">
                    <Canvas
                        camera={{ position: [0, 0, 1], fov: 50 }}
                        gl={{
                            alpha: true,
                            antialias: true,
                            preserveDrawingBuffer: false,
                            failIfMajorPerformanceCaveat: false, // Don't fail on slower devices
                        }}
                        onCreated={(state) => {
                            // Verify context is valid after creation
                            const gl = state.gl.getContext();
                            if (!gl || gl.isContextLost()) {
                                throw new Error('WebGL context lost or invalid');
                            }
                        }}
                    >
                        <GlowPlane
                            isActive={isActive}
                            isHovered={isHovered}
                            glowColor={glowColor}
                            intensity={intensity}
                            maskTexture={maskTexture}
                        />
                    </Canvas>
                </div>

                {/* Button content on top of shader */}
                <div className="shader-glow-content" ref={contentRef}>{children}</div>
            </div>
        </WebGLErrorBoundary>
    );
};

export default ShaderGlowButton;
